import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary for Panels
 * Provides graceful degradation when a panel component crashes
 */
class PanelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error(`Panel Error (${this.props.panelName || 'Unknown'}):`);
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState(prevState => ({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      const { panelName = 'Panel', minimal = false } = this.props;
      
      if (minimal) {
        // Minimal error display for small panels
        return (
          <div 
            className="panel-error-minimal"
            onClick={this.handleRetry}
            title={`${panelName} error - Click to retry`}
          >
            <AlertTriangle size={14} />
            <span>Error</span>
          </div>
        );
      }
      
      return (
        <div className="panel-error-boundary" data-testid="panel-error">
          <div className="panel-error-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="panel-error-content">
            <h4 className="panel-error-title">{panelName} Unavailable</h4>
            <p className="panel-error-message">
              {this.state.error?.message?.slice(0, 100) || 'Something went wrong'}
            </p>
          </div>
          <button 
            className="panel-error-retry"
            onClick={this.handleRetry}
            title="Retry loading this panel"
          >
            <RefreshCw size={14} />
            <span>Retry</span>
          </button>
          {this.state.retryCount > 0 && (
            <span className="panel-error-retry-count">
              Retried {this.state.retryCount}x
            </span>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper hook for functional components
 */
const withErrorBoundary = (WrappedComponent, panelName, minimal = false) => {
  return function ErrorBoundaryWrapper(props) {
    return (
      <PanelErrorBoundary panelName={panelName} minimal={minimal}>
        <WrappedComponent {...props} />
      </PanelErrorBoundary>
    );
  };
};

export { PanelErrorBoundary, withErrorBoundary };
export default PanelErrorBoundary;
